/**
 * Vocabulary schemas — the authored `LexiconDefinition` shape, as JSON-facing
 * Zod (plan §3; decision D16).
 *
 * @remarks
 * These schemas mirror `@orpheus/parser-engine`'s authored vocabulary types
 * 1:1 — same names, same shapes, same optionality. They CANNOT import them:
 * `content-schema`'s only dependency is `zod` (architecture.md §2.1, hard
 * rule), so the mirror is maintained by hand and drift-checked where both
 * packages are legally visible — a compile-time assignability test lands in
 * `content-loader` with R3 (Mike's call, 2026-08-12).
 *
 * Division of authority (plan §3): this schema is the AUTHORING gate — shape,
 * presence, non-blankness. The parser's `compileLexicon` remains the single
 * SEMANTIC authority (single-word constraints, duplicate/collision detection,
 * `movementIntentToken` referencing a declared intent, the reserved
 * `builtin:` namespace — decision D14). Constraints are deliberately not
 * duplicated here: two authorities drift; one authority plus a gate does not.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { nonBlankStringSchema } from './primitives.js';

/** Direct-object expectations for an intent. */
export const objectPolicySchema = z.enum(['forbidden', 'optional', 'required']);

/**
 * D134: which side of the room a verb reaches for when a noun answers on
 * both — `take` reaches for what is `present`, `drop` for what is `carried`;
 * `none` (the default when absent) asks. Mirrors the parser's
 * `ObjectPreference` exactly (the drift test compares the two declarations).
 */
export const objectPreferenceSchema = z.enum(['carried', 'none', 'present']);

/** One object preference — see {@link objectPreferenceSchema}. */
export type ObjectPreference = z.infer<typeof objectPreferenceSchema>;

/** Direct-object expectations for an intent: `look` forbids, `open` requires, `descend` may take one. */
export type ObjectPolicy = z.infer<typeof objectPolicySchema>;

/**
 * The roles a prepositional phrase can play (`toolRef` / `indirectObjectRef`)
 * — and, since D229, `direct_object`: a preposition that merely INTRODUCES
 * the verb's object (`hack at the tree`, `knock at the door`, `search
 * through the cases`), so the phrase after it is the direct object and
 * binds nothing. Legal only before any object word; a verb that forbids an
 * object cannot declare it.
 */
export const prepositionRoleSchema = z.enum(['direct_object', 'indirect_object', 'tool']);

/** One of the {@link prepositionRoleSchema} roles. */
export type PrepositionRole = z.infer<typeof prepositionRoleSchema>;

/** Declares that one preposition, after this intent, binds the following phrase to a role. */
export const prepositionBindingSchema = z
	.strictObject({
		bindsAs: prepositionRoleSchema,
		/**
		 * D261: the phrase after this preposition says where the direct object
		 * IS (`take the canister FROM locker m-12`). When the object's noun
		 * answers to more than one thing, the ones inside the thing this phrase
		 * names are preferred, and a question is asked only among them. Only an
		 * `indirect_object` locates; the parser refuses it on any other role.
		 */
		locatesObject: z.literal(true).optional(),
		preposition: nonBlankStringSchema,
	})
	.readonly();

/** One preposition-to-role declaration — see {@link prepositionBindingSchema}. */
export type PrepositionBinding = z.infer<typeof prepositionBindingSchema>;

/**
 * One direction the vocabulary understands, with its shorthand aliases.
 *
 * @remarks
 * **The overlay trap (D16, named by D137).** A game pack that re-declares a
 * base direction's `canonicalToken` REPLACES the base entry wholesale —
 * `{ "canonicalToken": "out", "aliases": ["back"] }` does not ADD `back`, it
 * deletes every alias the base gave `out`. To extend, list the base aliases
 * too. The loader warns, naming what was dropped, but it cannot tell a
 * deliberate drop from an accident; the same rule and the same warning
 * apply to {@link intentDefinitionSchema}.
 */
export const directionDefinitionSchema = z
	.strictObject({
		aliases: z.array(nonBlankStringSchema).readonly(),
		canonicalToken: nonBlankStringSchema,
		/**
		 * D244 (Act II §13.3): the direction that goes back the way this one
		 * came — `antispinward` for `spinward`, `aft` for `fore`. A pack's own
		 * direction words have no compass to borrow an opposite from, so the
		 * pack says it. It must name another declared direction's canonical
		 * token, and that direction must name this one back (the loader judges
		 * both against the merged vocabulary). Declared, it holds the rooms to
		 * it: an exit answering this word whose destination has no exit
		 * answering the opposite back is reported, because a ring that cannot
		 * be walked back the way it was walked out is usually a missed word.
		 * Absent — as for every base direction — nothing is judged.
		 */
		opposite: nonBlankStringSchema.optional(),
	})
	.readonly();

/** One authored direction — see {@link directionDefinitionSchema}. */
export type DirectionDefinition = z.infer<typeof directionDefinitionSchema>;

/**
 * One intent (verb) the vocabulary understands.
 *
 * @remarks
 * Re-declaring a base intent's `canonicalToken` replaces the base entry
 * wholesale — aliases AND preposition bindings — see the overlay trap on
 * {@link directionDefinitionSchema}.
 */
export const intentDefinitionSchema = z
	.strictObject({
		aliases: z.array(nonBlankStringSchema).readonly(),
		/**
		 * D227 (Calder's `the-road-climbs` §2): the direction this verb STANDS
		 * FOR when it stands alone — `climb` is `up`, so a bare `climb`, a
		 * `climb up` and a `climb up the road` all move by whatever exit answers
		 * to `up`, while `climb the rope` stays the verb the pack wrote and the
		 * rope's own answer. The link lives here, on the verb, because it is a
		 * fact about the language and not about any one exit or room: `climb`
		 * means up wherever English is spoken, as `ascend` and `descend` do —
		 * and those are plain direction aliases only because they take no
		 * object. Must name a direction's canonical token; legal only on a verb
		 * whose `objectPolicy` is `optional` (a verb that forbids objects IS a
		 * direction alias, and one that requires an object never stands alone).
		 * The lexicon compiler refuses it otherwise.
		 */
		bareDirection: nonBlankStringSchema.optional(),
		canonicalToken: nonBlankStringSchema,
		/**
		 * D35: the vocabulary RECOGNIZES this verb but the engine has no
		 * mechanics for it ("kick", "break", "drop" in v0). Parsing succeeds —
		 * including the object — and narrowing refuses honestly, naming both the
		 * verb and what it was aimed at. The alternative is pretending not to
		 * speak English at a player who typed a genre-standard directive.
		 */
		isUnsupported: z.literal(true).optional(),
		objectPolicy: objectPolicySchema,
		/**
		 * D134: the verb's side of the room on a two-sided noun. Absent means
		 * `none` — the parser asks. Dead on an intent that forbids objects; the
		 * lexicon compiler refuses it there.
		 */
		objectPreference: objectPreferenceSchema.optional(),
		/**
		 * D56: the PARTICLES this verb takes — the second half of a phrasal verb
		 * ("turn ON", "pick UP", "put DOWN").
		 *
		 * @remarks
		 * A particle is not a preposition, though English spells several of them
		 * the same. A preposition owns its object and cannot move ("look AT the
		 * door"); a particle belongs to the verb and CAN ("turn on the kettle" /
		 * "turn the kettle on"). With a pronoun the shift stops being optional
		 * and becomes mandatory: "turn it on" is the only grammatical form, and
		 * "turn on it" is not English.
		 *
		 * That is why declaring `turn on` as an ALIAS was the wrong model rather
		 * than a cheaper one. Alias matching wants two adjacent words, so the
		 * shifted and pronoun forms — the ones players actually type — could
		 * never match. Declaring the particle instead lets one intent accept
		 * every position, and DELETES the six phrasal aliases `turn` used to
		 * carry.
		 *
		 * A particle the intent declares outranks the same word used as a
		 * preposition elsewhere in the vocabulary: the intent in play is the
		 * authority on what its own words are doing.
		 */
		particles: z.array(nonBlankStringSchema).readonly().optional(),
		prepositionBindings: z.array(prepositionBindingSchema).readonly().optional(),
	})
	.readonly();

/** One authored intent — see {@link intentDefinitionSchema}. */
export type IntentDefinition = z.infer<typeof intentDefinitionSchema>;

/**
 * The complete authored vocabulary — the parser's entire knowledge of a
 * game's language, expressed as data. Two-layer merging (base pack + game
 * pack, decision D16) happens in `content-loader`; `compileLexicon` always
 * receives exactly one of these.
 */
export const lexiconDefinitionSchema = z
	.strictObject({
		directions: z.array(directionDefinitionSchema).readonly(),
		intents: z.array(intentDefinitionSchema).readonly(),
		/**
		 * D29: single tokens that OPEN a question ("what", "how", "am", "can").
		 * Drives the sentence-shape layer's failure-path routing; never affects
		 * a clause the imperative grammar already parses.
		 */
		interrogativeLeaders: z.array(nonBlankStringSchema).readonly().optional(),
		movementIntentToken: nonBlankStringSchema.optional(),
		/**
		 * D252: single tokens that open an ORDER to somebody (`tell`, `order`)
		 * in `tell <body> to <clause>` — shape data the parser reads only when
		 * the body is one the consumer marks addressable.
		 */
		orderLeaders: z.array(nonBlankStringSchema).readonly().optional(),
		/**
		 * D28: words the parser should RECOGNIZE as language without mapping
		 * them to anything — register data only (gibberish vs real-English
		 * failure voice), never command resolution. Single lowercase words.
		 */
		recognitionWords: z.array(nonBlankStringSchema).readonly().optional(),
		/**
		 * D29: single tokens whose whole short clause is social noise
		 * ("hello", "thanks", "yes") — shape data for the social register.
		 */
		socialWords: z.array(nonBlankStringSchema).readonly().optional(),
		/**
		 * D29: single tokens that OPEN a declaration about the speaker or world
		 * ("i", "we", "you") — shape data for the statement register.
		 */
		statementLeaders: z.array(nonBlankStringSchema).readonly().optional(),
		stopwords: z.array(nonBlankStringSchema).readonly().optional(),
	})
	.readonly();

/** One complete authored vocabulary — see {@link lexiconDefinitionSchema}. */
export type LexiconDefinition = z.infer<typeof lexiconDefinitionSchema>;
